import { useSyncExternalStore } from 'react';

/**
 * Hotbar drag-off lock preference (WoW-style action bar lock).
 *
 * - Default: `true` (locked) so clicking abilities in combat cannot accidentally
 *   drag them off the bar.
 * - While locked:
 *   - Dragging new abilities or usable items ONTO any bar slot is always allowed.
 *   - Dragging an existing slot OFF the bar (or moving it between slots) requires
 *     either holding `Shift` during dragStart OR unlocking via the lock button
 *     next to the bar's `⚙` button.
 */

const STORAGE_KEY = 'helix_hotbar_drag_locked';

let cached: boolean | null = null;
let shiftHeld = false;
let shiftListenersInstalled = false;
const listeners = new Set<() => void>();

function ensureShiftTracking(): void {
  if (shiftListenersInstalled || typeof window === 'undefined') return;
  shiftListenersInstalled = true;
  window.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      if (e.key === 'Shift' || e.code === 'ShiftLeft' || e.code === 'ShiftRight' || e.shiftKey) {
        shiftHeld = true;
      }
    },
    true,
  );
  window.addEventListener(
    'keyup',
    (e: KeyboardEvent) => {
      if (e.key === 'Shift' || e.code === 'ShiftLeft' || e.code === 'ShiftRight' || !e.shiftKey) {
        shiftHeld = Boolean(e.shiftKey);
      }
    },
    true,
  );
  window.addEventListener(
    'mousedown',
    (e: MouseEvent) => {
      shiftHeld = Boolean(e.shiftKey);
    },
    true,
  );
  window.addEventListener('blur', () => {
    shiftHeld = false;
  });
}

export function isShiftDragBypassActive(e?: { shiftKey?: boolean; nativeEvent?: { shiftKey?: boolean } }): boolean {
  ensureShiftTracking();
  return Boolean(e?.shiftKey || e?.nativeEvent?.shiftKey || shiftHeld);
}

function read(): boolean {
  ensureShiftTracking();
  if (cached === null) {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      cached = raw === null ? true : raw !== 'false';
    } catch {
      cached = true;
    }
  }
  return cached;
}

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  ensureShiftTracking();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isHotbarDragLocked(): boolean {
  return read();
}

export function setHotbarDragLocked(locked: boolean): void {
  cached = locked;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, locked ? 'true' : 'false');
    }
  } catch {
    /* ignore storage errors */
  }
  notify();
}

export function toggleHotbarDragLocked(): boolean {
  const next = !read();
  setHotbarDragLocked(next);
  return next;
}

export function resetHotbarDragLockedForTests(): void {
  cached = null;
  shiftHeld = false;
}

export function useHotbarDragLocked(): boolean {
  return useSyncExternalStore(subscribe, read, read);
}
